import React, { useMemo, useState, useRef } from 'react'
import { Layout } from 'app/theme/layout'
import { useQuery } from 'jsonapi-react'
import { Helmet } from 'react-helmet-async'
import StaticContent from 'app/utils/StaticContent'
import styles from './FoodTrucksPage.module.css'
import trucksRow from 'assets/img/trucks-row-light.png'
import CityAutoComplete from 'app/theme/forms/CityAutoComplete'
import illustration from 'assets/svg/truck-illustration.svg'
import { Spinner } from 'app/theme/Spinner'

const flagConfig = {
  "united-states": "🇺🇸",
  "united-kingdom": "🇬🇧",
  "canada": "🇨🇦",
  "ireland": "🇮🇪",
  "estonia": "🇪🇪",
  "russia": "🇷🇺",
  "japan": "🇯🇵",
  "china": "🇨🇳",
  "united-arab-emirates": "🇦🇪",
  "spain": "🇪🇸",
  "france": "🇫🇷",
  "australia": "🇦🇺",
  "default": "🏁",
}

const getChunks = (arr, size) => (
  Array.from({
    length: Math.ceil(arr.length / size)
  }, (v, i) =>
    arr.slice(i * size, i * size + size)
  )
)

export default function FoodTrucksPage(props) {
  if (props.staticContext) {
    props.staticContext.cacheMaxAge = 604800
  }

  const [selectedCity, setSelectedCity] = useState()
  return (
    <Layout>
      <Helmet>
        <title>Find and Book Food Trucks in Your City - Roaming Hunger</title>
        <meta name="description" content="Select a city to locate and book over 17978 of the best food trucks, carts and street food vendors on Roaming Hunger."></meta>
        <meta name="og:title" content="Find and Book Food Trucks in Your City"></meta>
      </Helmet>

      <div className="container flex flex-col items-center justify-center text-center my-10">
        <h1 className="text-black font-platform leading-none text-4xl">
          Find local food trucks near you
        </h1>
        <p className="text-gray-800 text-2xl font-light">
          Find and book food trucks, trailers, and carts in your city.
        </p>
        <div className="flex mt-5 shadow-lg bg-white border border-gray-400" >
          <CityAutoComplete
            value={selectedCity}
            onChange={city => {
              setSelectedCity(city)
            }}
          />
          <a
            href={`/food-trucks/${selectedCity?.url_slug}/`}
            className={["btn px-4 py-2 text-lg border-0", (selectedCity ? "btn-blue" : "btn-gray pointer-events-none")]}
            onClick={() => {
              // TODO: Show faux-error message
            }}
            children="Go →"
          />
        </div>

        <p className="text-gray-600 text-lg font-light mt-5">
        Use the search bar above to find food trucks in your city or use the country, state, and city lists below...
        </p>
      </div>

      <div
        className={[styles.trucks_row, 'w-full bg-repeat-x']}
        style={{backgroundImage: `url(${trucksRow})`}}
      />
      <StaticContent>
        <FoodTrucks />
      </StaticContent>
      <div className="bg-white py-5">
        <div className="container flex flex-col sm:flex-row justify-center items-center">
          <div>
            <h3 className="text-black text-2xl font-platform">
              Looking for <a href="/food-truck-catering/" className="text-navy">food truck catering</a> instead?
            </h3>
            <p className="py-4">
              Book a local food truck for your next event. <br />
              Protected by The <a target="_blank" href="/food-truck-catering/roaming-hunger-guarantee/" className="text-blue underline">100% Roaming Hunger Guarantee.</a> <br />
              Fast, easy and delicious.
            </p>
            <a
              href="/food-truck-catering/"
              className="btn btn-blue py-2 px-4 my-2"
            >
              Find Catering
            </a>
          </div>
          <div className="mt-4 sm:mt-0 w-full lg:w-5/12">
            <img src={illustration} alt="Roaming Hunger" title="Roaming Hunger" />
          </div>
        </div>
      </div>
    </Layout>

  )
}

function FoodTrucks(props) {
  const { data, isLoading } = useQuery(['countries', {
    include: [
      'regions',
      'cities',
    ],
    filter: {
      caterer_count: {
        gt: 0,
      },
      cities: {
        type: ['county', 'city'],
        active: true,
        caterer_count: {
          gt: 0,
        },
      },
    },
    fields: {
      countries: ['name', 'slug'],
      regions: ['name', 'slug', 'url_slug'],
      cities: ['name', 'url_slug', 'region_id']
    },
    page: {
      size: 999,
    },
    sort: [
      'regions.name',
      'cities.name',
    ],
  }], {
    hydrate: false,
  })

  const countries = useMemo(() => {
    if (!data) {
      return []
    }

    return data.map(country => {
      const regions = country.regions.map(r => ({ ...r, cities: [] }))

      country.cities.forEach(city => {
        const region = regions.find(r => r.id === String(city.region_id))

        if (region) {
          region.cities.push(city)
        } else {
          regions.push(city)
        }
      })

      return {
        ...country,
        regions,
      }
    }).filter(c => c.regions.length > 0).sort((a,b) => (
      b.regions.length - a.regions.length
    ))
  }, [data])

  return (
    <>
      <div className="container">
        <h2 className="text-black font-platform leading-none text-2xl">
          Browse Food Trucks Near You by City
        </h2>
        <p className="font-light text-lg pt-5">
          Find food trucks in your city or town by choosing your country and state.
          If you don’t see your city, choose the closest major metro area.
        </p>
      </div>

      <div className="container py-10">
        {isLoading ? <Spinner style={{height:"30px",width:"30px"}} className="text-blue"/> : (
          <div className="grid xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {countries.map(country => {
              return (
                <div
                  key={country.id}
                  className="my-5"
                  style={country.regions.length > 3 ? {gridColumn: "1 / -1"} : {}}
                >
                  {country.regions.length > 3 ? (
                    <ExpandableRegions country={country} open={country.slug == 'united-states' || false} />
                  ) : (
                    <>
                      <h3 className="text-navy font-platform leading-none text-xl">
                        {flagConfig[country.slug] || "🏁"} <span className="pl-2">{country.name}</span>
                      </h3>
                      {country.regions.map((region) => {
                        const cities = region.cities?.length ? region.cities : null
                        return cities ? (
                          <>
                            <summary key={region.id} className={[styles.summary, "mt-5"]}>
                              <h3 className="inline cursor-pointer hover:text-blue">
                                <a
                                  children={region.name}
                                  href={`/food-trucks/${region.url_slug}/`}
                                  className="pointer-events-none"
                                />
                              </h3>
                            </summary>
                            <ul className="list-disc mt-2 ml-6 text-lg">
                              {cities.map(city => (
                                <li key={city.id} className="py-2">
                                  <h4 className="hover:text-blue">
                                    <a href={`/food-trucks/${city.url_slug}/`}>
                                      {city.name}
                                    </a>
                                  </h4>
                                </li>
                              ))}
                            </ul>
                          </>
                        ) : (
                          <h3 key={region.id} className="cursor-pointer hover:text-blue mt-2">
                            <a
                              children={region.name}
                              href={`/food-trucks/${region.url_slug}/`}
                            />
                          </h3>
                        )
                      })}
                    </>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}

function ExpandableRegions({ country, open }) {
  const ref = useRef()
  return (
    <>
      <h3 className="text-navy font-platform leading-none text-xl">
        <span className="mr-2">
          {flagConfig[country.slug] || "🏁"}
        </span>
        <span className="mr-2">
          {country.name}
        </span>
      </h3>
      <details className="container mt-2 bg-white px-5 py-2 border" open={open}>
        <summary className="text-blue font-light font-platform cursor-pointer">
          Show/hide list of {country.region?.cities?.length ? "cities" : "regions"}
        </summary>
        <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 leading-loose">
          {country.regions.map(region => region.cities?.length ? (
            <details className={styles.region_details} key={region.id}>
              <summary className="cursor-pointer text-black hover:text-blue">
                {region.name}
              </summary>
              <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 leading-loose">
                {region.cities?.map(city => (
                  <li key={city.id}>
                    <a
                      href={`/food-trucks/${city.url_slug}/`}
                      className="font-light cursor-pointer hover:text-blue">
                      {city.name}
                    </a>
                  </li>
                ))}
              </ul>
            </details>
          ) : (
            <a
              key={region.id}
              href={`/food-trucks/${region.url_slug}/`}
              className={"cursor-pointer text-black hover:text-blue"}
            >
              {region.name}
            </a>
          ))}
        </ul>
      </details>
    </>
  )
}
